import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { RadioListImage } from 'app/components/shared/bb-radio-list/bb-radio-list.component';
import { Debounce } from 'app/decorators/debounce.decorator';
import { Channel, ChannelBlock, BasicList, ChannelBlockType } from 'app/interfaces/channel';
import { QueryParameter } from 'app/interfaces/queryString';
import { SapiQueryResponse, SapiService } from 'app/services/sapi.service';

@Component({
	selector: 'channel-details-general',
	templateUrl: './channel-details-general.component.html',
	styleUrls: ['./channel-details-general.component.scss']
})

export class ChannelDetailsGeneralComponent implements OnInit, OnChanges {
	@Input() channel: Channel;
	@Input() playerBlocks: ChannelBlock<ChannelBlockType>[];
	@Input() form: NgForm;
	@Input() backgroundColorPreset: BasicList[];

	@Output() onLogoChanged = new EventEmitter();

	colorThemeOptions: RadioListImage[];
	playerOptions: RadioListImage[];
	fontFamilyOptions: BasicList[] = [
		{ label: 'Montserrat', value: 'Montserrat' }, // default
		{ label: 'Lato', value: 'Lato' },
		{ label: 'Merriweather', value: 'Merriweather' },
		{ label: 'Open Sans', value: 'Open Sans' },
		{ label: 'Oswald', value: 'Oswald' },
		{ label: 'Playfair Display', value: 'Playfair Display' },
		{ label: 'Raleway', value: 'Raleway' },
		{ label: 'Roboto', value: 'Roboto' },
		{ label: 'Roboto Condensed', value: 'Roboto Condensed' },
		{ label: 'Source Sans Pro', value: 'Source Sans Pro' }
	];

	constructor(private translate: TranslateService, private sapiService: SapiService) {
		this.colorThemeOptions = [
			{ label: this.translate.instant('LIGHT'), value: 'light', imageUrl: '/resources/img/channels/Light-mode.svg' }, // default
			{ label: this.translate.instant('DARK'), value: 'dark', imageUrl: '/resources/img/channels/Dark-mode.svg' }
		];

		this.playerOptions = [
			{ label: this.translate.instant('VIDEOPLAYER'), value: 'player', imageUrl: '/resources/img/channels/Videoplayer.svg' },
			{ label: this.translate.instant('DEEPLINK'), value: 'deeplink', imageUrl: '/resources/img/channels/Deeplink.svg' },
			{ label: this.translate.instant('DETAIL_PAGE'), value: 'detailPage', imageUrl: '/resources/img/channels/Channel-overlay.svg' }, // default
		];
	}

	@Debounce(200)
	validateNameUnique(channelName: string) {
		const queryParams: QueryParameter[] = [{ name: 'q', values: ['name:' + JSON.stringify(channelName)] }];

		this.sapiService.query('channel', queryParams).subscribe((sapiQueryResponse: SapiQueryResponse) => {
			if (sapiQueryResponse && sapiQueryResponse.totalAmount > 0) {
				if (sapiQueryResponse.totalAmount === 1 && sapiQueryResponse.items[0].id === this.channel.id) {
					return;
				}
				this.form.form.controls.channel_name.setErrors({ 'not-unique': true });
				this.form.form.controls.channel_name.markAsTouched();
				this.form.form.controls.channel_name.markAsDirty();
			}
		});
	}

	ngOnInit() { }

	// disable/enable playInOption videoplayer depending on whether a player (block) is available
	ngOnChanges(changes: SimpleChanges): void {
		if (changes.playerBlocks) {
			const playerBlockAvailable = !!changes.playerBlocks.currentValue.length;
			if (playerBlockAvailable) {
				delete this.playerOptions[0].disabled;
			} else {
				this.playerOptions[0].disabled = true;
			}
		}
	}

	// set colors based on theme, unless you already set a custom color
	setThemeColors(colorTheme: 'dark' | 'light') {
		const defaultTextColors = ['ffffff', '141414'];

		if (defaultTextColors.includes(this.form.form.controls.channel_primaryFontColor.value)) {
			this.form.form.controls.channel_primaryFontColor.setValue(colorTheme === 'dark' ? 'ffffff' : '141414');
		}

		if (defaultTextColors.includes(this.form.form.controls.channel_secondaryFontColor.value)) {
			this.form.form.controls.channel_secondaryFontColor.setValue(colorTheme === 'dark' ? 'ffffff' : '141414');
		}

		if (['1e1e1e', 'ffffff'].includes(this.form.form.controls.channel_backgroundColor.value)) {
			this.form.form.controls.channel_backgroundColor.setValue(colorTheme === 'dark' ? '1e1e1e' : 'ffffff');
		}
	}

	public changeChannelLogo(id: string) {
		this.form.form.markAsDirty();
		this.onLogoChanged.emit(id);
	}
}
